/** @jsxImportSource @emotion/react */
import infoImg from "../../assets/icons/info.svg";
import * as React from "react";
import { LargeText, SmallText } from "../../styles/styles";
import { isNilOrEmpty } from "../../language/module";
import { TooltipFC } from "../../layout/TooltipFC";
import { RED_CREEK } from "../../styles/colors";
import { css, SerializedStyles } from "@emotion/react";

export const ROW_GROUP = css`
  flex-direction: row;
  align-items: center;

  span {
    margin-bottom: 0;
    margin-right: 10px;
  }
`;

export const REVERSE_ROW_GROUP = css`
  flex-direction: row-reverse;
  align-items: center;

  span {
    margin-bottom: 0;
    margin-left: 10px;
  }
`;

const subLabelStyle = css`
  font-weight: normal;
  ${SmallText};
`;

const infoStyle = css`
  width: 20px;
  height: 20px;
  margin-bottom: -3px; /* align image with text */
  margin-left: 5px;
  margin-right: 10px;
  pointer-events: initial;
`;

const requiredStyle = css`
  color: ${RED_CREEK};
  margin-left: 4px;
  font-weight: bold;
  ${LargeText};
`;

/**
 * LabelGroup: a component that renders an input along with a label. You don't have to worry about giving the input a
 * particular ID to match the label or anything, just wrap your input in this component and you're good to go. Renders
 * the label above the input in column form (you can override with css).
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class LabelGroup extends React.PureComponent<{
  label: string;
  subLabel?: string;
  title?: string;
  info?: string;
  rootCss?: SerializedStyles;
  children: React.ReactNode;
  subLabelCss?: SerializedStyles;
  showRequired?: boolean;
  requiredTitle?: string;
  requiredCss?: SerializedStyles;
}> {
  /**
   * This was added for IRT-406. Opening the tooltip would not trigger the click action on the label, but closing the
   * tooltip would still trigger it. This prevents the tooltip from bubbling into other events.
   */
  private onTooltipClick = (event) => event.preventDefault();

  public render = () => (
    <label
      title={this.props.title}
      css={css`
        display: flex;
        flex-direction: column;
        ${this.props.rootCss};
      `}
    >
      <span
        css={css`
          display: inline-block;
          ${LargeText};
          font-weight: bold;
          margin-bottom: 10px;
        `}
      >
        {this.props.label}{" "}
        {!isNilOrEmpty(this.props.subLabel) && (
          <span css={this.props.subLabelCss || subLabelStyle}>
            {this.props.subLabel}
          </span>
        )}
        {this.props.showRequired && (
          <span css={this.props.requiredCss ?? requiredStyle} title={this.props.requiredTitle}>
            *
          </span>
        )}
        {this.props.info && (
          <TooltipFC
            content={<span>{this.props.info}</span>}
            children={
              <img
                alt="ⓘ"
                src={infoImg}
                css={infoStyle}
                onClick={this.onTooltipClick}
              />
            }
          />
        )}
      </span>
      {this.props.children}
    </label>
  );
}
